import { ArrowLeftRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { getAllTransactionsCount } from "@/api/services/allTransactionGraphqlService";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useApp } from "@/contexts/AppContext";
import { DEMO_TRANSACTIONS } from "@/data/demo-data";
import { useEntityFilter } from "@/hooks/useEntityFilter";
import { usePermissions } from "@/hooks/usePermissions";

interface UncategorizedItemsCardProps {
  onNavigate?: (page: string, data?: Record<string, unknown>) => void;
}

export default function UncategorizedItemsCard({
  onNavigate,
}: UncategorizedItemsCardProps) {
  const { hasPermission } = usePermissions();
  const { filterByEntity } = useEntityFilter();
  const { mode } = useApp();
  const [liveUncategorizedCount, setLiveUncategorizedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (mode === "live") {
      setIsLoading(true);
      getAllTransactionsCount({ category: { eq: "Uncategorised" } })
        .then((count) => {
          setLiveUncategorizedCount(count);
        })
        .catch((err) => {
          console.error("Failed to fetch uncategorized count:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [mode]);

  if (!hasPermission("view_uncategorized_items_card")) {
    return null;
  }

  // In demo mode, use demo data; in live mode, use API data
  const uncategorizedCount =
    mode === "live"
      ? liveUncategorizedCount
      : filterByEntity(DEMO_TRANSACTIONS).filter(
          (txn) => txn.category === "Uncategorised",
        ).length;

  const isError = uncategorizedCount > 0;
  const badgeBg = isError ? "var(--mc-error-bg)" : "var(--mc-success-bg)";
  const badgeIcon = isError ? "var(--mc-error-icon)" : "var(--mc-success-icon)";

  return (
    <Card
      className="h-full"
      style={{
        borderLeft: `3px solid ${isError ? "var(--mc-error-border)" : "var(--mc-success-border)"}`,
      }}
    >
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top row — title + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">
              Uncategorised Transactions
            </span>
            <InfoTooltip
              title="Uncategorised Transactions"
              content="Transactions that haven't been assigned a category yet. Categorising these improves forecast accuracy and ensures your reports are complete. Click Categorise Now to work through them."
            />
          </div>
          <div
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: badgeBg }}
          >
            <ArrowLeftRight className="w-5 h-5" style={{ color: badgeIcon }} />
          </div>
        </div>

        {/* Value */}
        {isLoading && mode === "live" ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mt-2" />
        ) : (
          <div className="text-2xl font-bold text-neutral-900 tracking-tight mt-2">
            {uncategorizedCount}
          </div>
        )}

        {/* Sub-text */}
        <div className="mt-1">
          <button
            type="button"
            className="text-xs font-semibold text-neutral-900 hover:underline hover:underline-offset-2 cursor-pointer"
            onClick={() =>
              onNavigate?.("transactions", { initialFilter: "uncategorized" })
            }
          >
            Categorise Now
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
